// apps/web/src/lib/estoque/saldos-clientes.ts — Fase 13, Plano 14 (EST-08, EST-09, EST-11, D-22, D-23).
// Lógica pura da aba "Material de clientes" da tela de Saldos: aba na URL, filtros (URL e query da API), rótulos dos
// eventos de localização do lote e validação da transferência manual de lote. Quem filtra, ordena e pagina é o servidor
// (`GET /api/v1/estoque/saldos/clientes`); nada aqui recalcula saldo nem valor da NF.
import type { EventoLocalizacaoLote, HistoricoLoteLinha } from '@pieltec/contracts';
import { formatarDataHora } from '../vendas/formatacao';
import { TAMANHO_PAGINA_SALDOS } from './saldos';

export type AbaSaldos = 'proprio' | 'clientes';

/** Aba ativa a partir de `location.search`: `?aba=clientes`; qualquer outro valor (ou nenhum) é o estoque próprio. */
export function abaDaUrl(search: string): AbaSaldos {
  return new URLSearchParams(search).get('aba') === 'clientes' ? 'clientes' : 'proprio';
}

export interface FiltrosSaldosClientes {
  busca: string;
  /** UUID do cliente; vazio = todos. */
  clienteId: string;
  /** UUID da localização; vazio = todas. */
  localizacaoId: string;
  mostrarSemSaldo: boolean;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const LIMITE_BUSCA = 100;

export function filtrosClientesVazios(): FiltrosSaldosClientes {
  return { busca: '', clienteId: '', localizacaoId: '', mostrarSemSaldo: false };
}

/** Há algum filtro além do padrão (sem busca, todos os clientes e locais, só lotes com saldo)? */
export function filtrosClientesAtivos(filtros: FiltrosSaldosClientes): boolean {
  return filtros.busca.trim() !== '' || filtros.clienteId !== '' || filtros.localizacaoId !== '' || filtros.mostrarSemSaldo;
}

interface OpcoesQueryClientes {
  pagina?: number;
  tamanhoPagina?: number;
}

/**
 * Query string (sem `?`) com só os filtros ativos. Usa `URLSearchParams`: nenhum valor do usuário é concatenado na mão.
 * `tamanhoPagina` padrão 50 (a API aceita até 100).
 */
export function montarQuerySaldosClientes(filtros: FiltrosSaldosClientes, opcoes: OpcoesQueryClientes = {}): string {
  const params = new URLSearchParams();
  params.set('pagina', String(opcoes.pagina ?? 1));
  params.set('tamanhoPagina', String(opcoes.tamanhoPagina ?? TAMANHO_PAGINA_SALDOS));
  const busca = filtros.busca.trim();
  if (busca !== '') params.set('busca', busca.slice(0, LIMITE_BUSCA));
  if (filtros.clienteId !== '') params.set('clienteId', filtros.clienteId);
  if (filtros.localizacaoId !== '') params.set('localizacaoId', filtros.localizacaoId);
  if (filtros.mostrarSemSaldo) params.set('mostrarSemSaldo', 'true');
  return params.toString();
}

/** Filtros da aba clientes a partir de `location.search`; UUID inválido volta ao padrão em vez de gerar 400. */
export function lerFiltrosClientesDaUrl(search: string): FiltrosSaldosClientes {
  const params = new URLSearchParams(search);
  const filtros = filtrosClientesVazios();
  filtros.busca = (params.get('busca') ?? '').trim().slice(0, LIMITE_BUSCA);
  const cliente = params.get('cliente') ?? '';
  filtros.clienteId = UUID.test(cliente) ? cliente : '';
  const localizacao = params.get('localizacao') ?? '';
  filtros.localizacaoId = UUID.test(localizacao) ? localizacao : '';
  filtros.mostrarSemSaldo = params.get('semSaldo') === '1';
  return filtros;
}

/** `?aba=clientes&busca=...` para `history.replaceState`; a aba clientes sempre marca `aba=clientes`. */
export function escreverFiltrosClientesNaUrl(filtros: FiltrosSaldosClientes): string {
  const params = new URLSearchParams();
  params.set('aba', 'clientes');
  const busca = filtros.busca.trim();
  if (busca !== '') params.set('busca', busca);
  if (filtros.clienteId !== '') params.set('cliente', filtros.clienteId);
  if (filtros.localizacaoId !== '') params.set('localizacao', filtros.localizacaoId);
  if (filtros.mostrarSemSaldo) params.set('semSaldo', '1');
  return `?${params.toString()}`;
}

const ROTULOS_EVENTO_LOTE: Readonly<Record<EventoLocalizacaoLote, string>> = {
  RECEBIMENTO_NF: 'Recebimento da NF',
  INICIO_ETAPA: 'Início da primeira etapa',
  CQ: 'CQ',
  CONCLUSAO: 'Conclusão',
  MANUAL: 'Manual',
  HERANCA: 'Herdada do lote de origem',
};

/** Rótulo do evento de mudança de local do lote (D-22). */
export function rotuloEventoLote(evento: EventoLocalizacaoLote): string {
  return ROTULOS_EVENTO_LOTE[evento];
}

/** "{rótulo do evento} em dd/MM/yyyy HH:mm" (America/Sao_Paulo), a "última mudança" da linha expandida do lote. */
export function textoUltimaMudanca(evento: EventoLocalizacaoLote, criadoEm: string): string {
  return `${rotuloEventoLote(evento)} em ${formatarDataHora(criadoEm)}`;
}

// ---------------------------------------------------------------------------------------------------------------------
// Histórico do lote e transferência manual de lote (EST-09, D-22)
// ---------------------------------------------------------------------------------------------------------------------

const PREFIXO_AUTOMATICA = 'Automática — ';

/** Rótulo do evento como a API o escreve -> rótulo do UI-SPEC (a API ainda diz "Início de etapa" e "Controle de qualidade"). */
const EVENTO_DA_API_PARA_UI: Readonly<Record<string, string>> = {
  'Recebimento da NF': rotuloEventoLote('RECEBIMENTO_NF'),
  'Início de etapa': rotuloEventoLote('INICIO_ETAPA'),
  'Controle de qualidade': rotuloEventoLote('CQ'),
  Conclusão: rotuloEventoLote('CONCLUSAO'),
  'Herança do lote de origem': rotuloEventoLote('HERANCA'),
};

const ROTULOS_MOVIMENTO_LOTE: Readonly<Record<string, string>> = {
  ENTRADA: 'Entrada',
  SAIDA: 'Saída',
  CONSUMO: 'Consumo',
  ESTORNO: 'Estorno',
  SINISTRO: 'Sinistro',
  TRANSFERENCIA: 'Transferência',
};

/**
 * Texto da coluna "Tipo" do histórico do lote. Mudança de local do processo: "Automática — {Recebimento da NF / Início da
 * primeira etapa / CQ / Conclusão}"; transferência manual: "Manual"; movimento de quantidade: o rótulo do tipo.
 */
export function rotuloLinhaHistoricoLote(linha: Pick<HistoricoLoteLinha, 'origem' | 'tipo' | 'automatica'>): string {
  if (linha.origem === 'MOVIMENTO') return ROTULOS_MOVIMENTO_LOTE[linha.tipo] ?? linha.tipo;
  if (!linha.automatica) return 'Manual';
  const evento = linha.tipo.startsWith(PREFIXO_AUTOMATICA) ? linha.tipo.slice(PREFIXO_AUTOMATICA.length) : linha.tipo;
  return `${PREFIXO_AUTOMATICA}${EVENTO_DA_API_PARA_UI[evento] ?? evento}`;
}

export const MENSAGEM_ESCOLHA_DESTINO_LOTE = 'Escolha a localização de destino.';
export const MENSAGEM_ORIGEM_IGUAL_DESTINO_LOTE = 'Origem e destino não podem ser iguais.';
export const MENSAGEM_INFORME_MOTIVO_LOTE = 'Informe o motivo.';

export interface EntradaTransferenciaLote {
  /** Localização de destino escolhida ('' = nenhuma). */
  readonly para: string;
  /** Localização atual do lote ('' = desconhecida: o servidor decide). */
  readonly atual: string;
  readonly motivo: string;
}

export interface ErrosTransferenciaLote {
  readonly para?: string;
  readonly motivo?: string;
}

/** Valida a transferência manual do lote inteiro: destino diferente do atual e motivo obrigatório (D-22, EST-09). */
export function validarTransferenciaLote(entrada: EntradaTransferenciaLote): ErrosTransferenciaLote {
  const erros: { para?: string; motivo?: string } = {};
  if (entrada.para === '') erros.para = MENSAGEM_ESCOLHA_DESTINO_LOTE;
  else if (entrada.atual !== '' && entrada.para === entrada.atual) erros.para = MENSAGEM_ORIGEM_IGUAL_DESTINO_LOTE;
  if (entrada.motivo.trim() === '') erros.motivo = MENSAGEM_INFORME_MOTIVO_LOTE;
  return erros;
}
